import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { getNotificationAppearance, formatNotificationTime } from '../../utils/notificationAppearance';
import { getNotificationText } from '../../utils/notificationText';
import Skeleton from '../ui/Skeleton';

/**
 * Eine Zeile der Benachrichtigungsliste, gemeinsam für NotificationsPage und
 * NotificationBell (Oktober 2026, iOS 1.0.3).
 *
 * Eine Struktur: neutrales Icon links (ohne Kreis, ohne Fläche), Titel, Text
 * und Zeit in der Mitte, rechts genau ein Punkt bei ungelesen. Keine Tönung,
 * keine Typfarben, keine Aktions-Pillen: Die Zeile selbst ist das Ziel.
 * Keine Haptik beim Tipp, denn der Tipp navigiert nur.
 *
 * Der Anzeigetext läuft durch getNotificationText, damit alte Datensätze
 * ohne Emojis erscheinen (die Datenbank bleibt unverändert).
 */
const NotificationRow = ({ notification, onSelect }) => {
  const { title, message } = useMemo(
    () => getNotificationText(notification),
    [notification],
  );
  const { Icon } = getNotificationAppearance(notification.type);
  const unread = notification.status === 'UNREAD';

  return (
    <button
      type="button"
      onClick={() => onSelect(notification)}
      className="flex w-full items-start gap-3 border-b border-border px-4 py-3.5 text-left transition-colors duration-fast active:bg-surface-raised"
    >
      <Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-faint" aria-hidden="true" />

      <span className="min-w-0 flex-1">
        <span className={`block text-sm text-foreground ${unread ? 'font-semibold' : 'font-normal'}`}>
          {title}
        </span>
        {message && (
          <span className="mt-0.5 block text-sm text-muted-foreground line-clamp-2">{message}</span>
        )}
        <span className="mt-1 block text-meta text-faint">
          {formatNotificationTime(notification.createdAt)}
        </span>
      </span>

      {unread && (
        <span className="mt-1.5 flex-shrink-0">
          <span className="block h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
          <span className="sr-only">Ungelesen</span>
        </span>
      )}
    </button>
  );
};

NotificationRow.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    status: PropTypes.string,
    title: PropTypes.string,
    message: PropTypes.string,
    createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
};

/** Ladezustand in Zeilenhöhe, damit beim Eintreffen der Liste nichts springt. */
export const NotificationRowSkeleton = ({ count = 3 }) => (
  <div aria-busy="true">
    <span className="sr-only">Benachrichtigungen werden geladen …</span>
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="flex items-start gap-3 border-b border-border px-4 py-3.5" aria-hidden="true">
        <Skeleton.Circle size="h-5 w-5" className="mt-0.5 flex-shrink-0" />
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton.Line width="w-1/2" />
          <Skeleton.Line width="w-5/6" className="h-3" />
          <Skeleton.Line width="w-16" className="h-3" />
        </div>
      </div>
    ))}
  </div>
);

NotificationRowSkeleton.propTypes = { count: PropTypes.number };

export default NotificationRow;
